/**
 * DecisionTimer - "you have a minute".
 *
 * - `TimerBar` (everyone): the countdown while one is up, counted on this
 *   screen from the moment the GM started it. At zero it says "Time!" for a
 *   few seconds and then goes by itself; whoever directs may dismiss it at
 *   any moment from the bar, and their screen clears the record when it goes.
 * - `TimerControl` (whoever directs, in the story column): start one of a
 *   few lengths, or clear the one that is up.
 *
 * It enforces nothing.
 */
import { useEffect, useState } from 'react';
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';
import story from '../../styles/Story.module.css';
import { TIMER_LENGTHS, clockText, lengthLabel, secondsLeft, timerShows } from '../../engine/narrative/timer';

/** How often the display looks at the clock, in ms. The seconds it shows come from the timestamp, not from counting ticks. */
const LOOK_EVERY_MS = 250;

/** This screen's clock, looked at while there is a timer to show. */
function useNow(timer) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!timer) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), LOOK_EVERY_MS);
    return () => clearInterval(id);
  }, [timer]);
  return now;
}

/**
 * @param {object} props
 * @param {import('../../engine/narrative/timer').DecisionTimer | null | undefined} props.timer
 * @param {() => void} [props.onClear] - given to whoever directs: dismiss it, and clear the record
 */
export function TimerBar({ timer, onClear }) {
  const now = useNow(timer);
  const shows = !!timer && timerShows(timer, now);
  // Let go of on the director's screen: the record goes too, so nobody arriving later finds it.
  const spent = !!timer && !shows;
  useEffect(() => {
    if (spent) onClear?.();
  }, [spent, onClear]);
  if (!shows) return null;
  const left = secondsLeft(timer, now);
  return (
    <div className={styles.calls} data-testid="decision-timer" data-up={left === 0 || undefined}>
      <div className={styles.callLine}>
        <span role="timer" aria-live="off" aria-label={left > 0 ? `${left} seconds to decide` : 'Time is up'}>
          {left > 0 ? `Decide: ${clockText(left)}` : 'Time!'}
        </span>
        {onClear && (
          <button type="button" className={common.btnSmall} data-testid="timer-dismiss" onClick={onClear}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {{ value: object|null, start: (seconds: number) => void, clear: () => void }} props.timer
 */
export function TimerControl({ timer }) {
  return (
    <section className={story.section} aria-label="Decision timer">
      <h3 className={story.caption}>Decision timer</h3>
      <div className={story.tools}>
        {TIMER_LENGTHS.map((seconds) => (
          <button
            key={seconds}
            type="button"
            className={common.btnSmall}
            data-testid={`timer-start-${seconds}`}
            onClick={() => timer.start(seconds)}
          >
            {lengthLabel(seconds)}
          </button>
        ))}
        <button
          type="button"
          className={common.btnSmall}
          data-testid="timer-clear"
          disabled={!timer.value}
          onClick={timer.clear}
        >
          Clear
        </button>
      </div>
    </section>
  );
}
